import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Paper,
} from "@mantine/core";
import { useClient } from "../../../context/ClientContext";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { IconStethoscope, IconPlus, IconTrash } from "@tabler/icons-react";
import Konva from "konva";
import { useApp } from "../../../context/AppContext";
import { DocumentVersion } from "../../../types/salonDocument";
import {
  parseChartDocumentFromPngBytes,
  parseChartDocumentFromVersionMetadata,
} from "./utils/chartMarkupDocument";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";
import DiagramLibrary from "./DiagramLibrary";
import ChartEditor from "./ChartEditor";
import { ChartDocument, DiagramSelection, SavedChartMeta } from "./types/chart";
import { useChartPersistence } from "./hooks/useChartPersistence";
import primaryAction from "../../common/primaryFilledButton.module.css";
import styles from "./MedicalChartsTab.module.css";

interface MedicalChartsTabProps {
  clientId?: string;
  /** Incremented from parent when client documents change so this tab refetches chart list. */
  chartsListInvalidateKey?: number;
  /** Called after a chart delete succeeds so the Documents tab can refetch its list. */
  onChartDeleted?: () => void;
}

interface EditorState {
  imageUrl: string;
  imageName: string;
  source: "preset" | "upload";
  /** If editing a previously saved chart, its document key */
  existingDocumentKey?: string;
  /** If editing a previously saved chart, the loaded document */
  existingDocument?: ChartDocument;
}

export default function MedicalChartsTab({
  clientId,
  chartsListInvalidateKey = 0,
  onChartDeleted,
}: MedicalChartsTabProps) {
  const { getSalonDocumentDetail } = useApp();
  const { clientsById } = useClient();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const salonId = businessInfo?.id || "";

  const clientRecord = clientId ? clientsById[clientId] : undefined;
  const clientDisplayName = clientRecord
    ? `${clientRecord.first_name || ""} ${clientRecord.last_name || ""}`.trim()
    : "";

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryDefaultTab, setLibraryDefaultTab] = useState<
    "presets" | "upload"
  >("presets");
  const [editorState, setEditorState] = useState<EditorState | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<SavedChartMeta | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpeningChart, setIsOpeningChart] = useState(false);
  const openingChartGuard = useRef(false);

  const { charts, loading, saveChart, loadChart, deleteChart, refreshCharts } =
    useChartPersistence(salonId, clientId || "");

  // Load charts on mount
  useEffect(() => {
    if (salonId && clientId) {
      refreshCharts();
    }
  }, [salonId, clientId, refreshCharts]);

  // Refetch when client documents change elsewhere (e.g. delete on Documents tab).
  useEffect(() => {
    if (!salonId || !clientId) return;
    if (chartsListInvalidateKey === 0) return;
    void refreshCharts();
  }, [chartsListInvalidateKey, salonId, clientId, refreshCharts]);

  const handleSelection = useCallback((selection: DiagramSelection) => {
    if (selection.source === "preset") {
      setEditorState({
        imageUrl: selection.diagram.imageUrl,
        imageName: selection.diagram.name,
        source: "preset",
      });
    } else {
      setEditorState({
        imageUrl: selection.imageUrl,
        imageName: selection.imageName,
        source: "upload",
      });
    }
  }, []);

  const handleOpenLibrary = useCallback(
    (tab: "presets" | "upload" = "presets") => {
      setLibraryDefaultTab(tab);
      setLibraryOpen(true);
    },
    [],
  );

  const handleBack = useCallback(() => {
    setEditorState(null);
  }, []);

  const handleSave = useCallback(
    async (document: ChartDocument, stage: Konva.Stage) => {
      try {
        await saveChart(document, stage, editorState?.existingDocumentKey);
        notifySuccess("Your chart markup has been saved successfully.", {
          title: "Chart saved",
        });
        setEditorState(null);
      } catch (error) {
        console.error("Failed to save chart:", error);
        notifyError(
          error instanceof Error
            ? error.message
            : "Failed to save chart. Please try again.",
          { title: "Save failed" },
        );
        // Re-throw so the editor keeps its dirty state and stays open for retry.
        throw error;
      }
    },
    [saveChart, editorState?.existingDocumentKey],
  );

  const handleEditChart = useCallback(
    async (chart: SavedChartMeta) => {
      if (openingChartGuard.current) return;
      openingChartGuard.current = true;
      setIsOpeningChart(true);
      try {
        const doc = await loadChart(chart.documentKey);
        if (doc) {
          setEditorState({
            imageUrl: doc.baseImageRef,
            imageName: chart.title,
            source: doc.baseImageSource === "preset" ? "preset" : "upload",
            existingDocumentKey: chart.documentKey,
            existingDocument: doc,
          });
        }
      } catch (error) {
        console.error("Failed to load chart:", error);
        notifyError("Failed to load chart for editing.", {
          title: "Load failed",
        });
      } finally {
        openingChartGuard.current = false;
        setIsOpeningChart(false);
      }
    },
    [loadChart],
  );

  const handleLoadVersions = useCallback(async (): Promise<{
    versions: DocumentVersion[];
    currentVersionId?: number;
  }> => {
    if (!salonId || !editorState?.existingDocumentKey) {
      return { versions: [] };
    }
    const detail = await getSalonDocumentDetail(
      salonId,
      editorState.existingDocumentKey,
      clientId,
    );
    return {
      versions: detail?.versions || [],
      currentVersionId: detail?.currentVersionId,
    };
  }, [
    salonId,
    clientId,
    editorState?.existingDocumentKey,
    getSalonDocumentDetail,
  ]);

  const handleLoadVersion = useCallback(
    async (version: DocumentVersion): Promise<ChartDocument | null> => {
      // Legacy saves stored the chart JSON in version metadata; new saves
      // embed it in the PNG body via a tEXt chunk. Try metadata first
      // (cheap), then fall back to fetching and parsing the body bytes.
      const fromMeta = parseChartDocumentFromVersionMetadata(
        version.metadata ?? undefined,
      );
      if (fromMeta) return fromMeta;
      if (!version.url) return null;
      const res = await fetch(version.url);
      if (!res.ok) return null;
      const buffer = await res.arrayBuffer();
      return parseChartDocumentFromPngBytes(new Uint8Array(buffer));
    },
    [],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteChart(deleteTarget.documentKey);
      notifySuccess("The chart has been removed.", { title: "Chart deleted" });
      onChartDeleted?.();
    } catch (error) {
      console.error("Failed to delete chart:", error);
      notifyError("Failed to delete chart. Please try again.", {
        title: "Delete failed",
      });
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteChart, onChartDeleted]);

  // Editor view (fullscreen modal)
  if (editorState) {
    return (
      <div className={styles.container}>
        <Modal
          opened
          onClose={handleBack}
          fullScreen
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          padding={0}
          styles={{
            body: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "#f9fafb",
              padding: 0,
            },
            content: { height: "100%", background: "#f9fafb" },
          }}
        >
          <ChartEditor
            key={`${editorState.imageUrl}-${editorState.existingDocumentKey ?? "new"}`}
            baseImageUrl={editorState.imageUrl}
            diagramName={editorState.imageName}
            clientDisplayName={clientDisplayName || undefined}
            onBack={handleBack}
            onSave={salonId && clientId ? handleSave : undefined}
            initialDocument={editorState.existingDocument}
            onLoadVersions={
              editorState.existingDocumentKey ? handleLoadVersions : undefined
            }
            onLoadVersion={
              editorState.existingDocumentKey ? handleLoadVersion : undefined
            }
          />
        </Modal>
      </div>
    );
  }

  // List / empty view
  const hasCharts = charts.length > 0;

  return (
    <div className={styles.container}>
      {isOpeningChart ? (
        <div
          className={styles.openingOverlay}
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader color="var(--brand-teal)" size="lg" type="oval" />
          <Text size="sm" c="dimmed" fw={500}>
            Opening chart…
          </Text>
        </div>
      ) : null}
      <div className={styles.chartsShell}>
        <div className={styles.pageHeader}>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="md"
          >
            <div className={styles.pageHeaderText}>
              <Text component="h2" className={styles.pageTitle}>
                Medical Charts
              </Text>
              <Text className={styles.pageSubtitle}>
                Attach diagrams, mark them up, and track treatment history.
              </Text>
            </div>
            <Button
              variant="outline"
              color="teal"
              size="sm"
              radius="md"
              className={`${primaryAction.primaryOutline} ${styles.addChartButton}`}
              leftSection={<IconPlus size={16} stroke={1.75} />}
              onClick={() => handleOpenLibrary("presets")}
            >
              Add Chart
            </Button>
          </Group>
        </div>
        <div className={styles.headerDivider} aria-hidden />

        {loading ? (
          <Text c="dimmed" ta="center" py="xl">
            Loading charts...
          </Text>
        ) : hasCharts ? (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {charts.map((chart) => (
              <Paper
                key={chart.documentKey}
                className={styles.chartCard}
                shadow="sm"
                radius="md"
                withBorder
                onClick={() => handleEditChart(chart)}
              >
                <div className={styles.chartCardImage}>
                  {chart.renderUrl ? (
                    <img
                      src={chart.renderUrl}
                      alt={chart.title}
                      className={styles.chartCardImg}
                    />
                  ) : (
                    <IconStethoscope
                      size={32}
                      color="var(--mantine-color-gray-4)"
                    />
                  )}
                </div>
                <div className={styles.chartCardFooter}>
                  <div>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {chart.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(chart.createdAt).toLocaleDateString()}
                    </Text>
                  </div>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(chart);
                    }}
                    aria-label="Delete chart"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </div>
              </Paper>
            ))}
          </SimpleGrid>
        ) : (
          <div className={styles.emptyBody}>
            <div className={styles.emptyState}>
              <IconStethoscope size={40} color="var(--mantine-color-gray-5)" />
              <Stack align="center" gap={4}>
                <Text fw={600} size="sm">
                  No charts attached yet
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={360}>
                  Select a diagram from the library or upload your own image,
                  then mark it up with annotations to track treatment areas.
                </Text>
              </Stack>
              <Group gap="sm" justify="center" wrap="wrap">
                <Button
                  variant="outline"
                  color="teal"
                  size="sm"
                  radius="md"
                  className={`${primaryAction.primaryOutline} ${styles.emptyCta}`}
                  onClick={() => handleOpenLibrary("presets")}
                >
                  Browse diagram library
                </Button>
                <Button
                  variant="outline"
                  color="teal"
                  size="sm"
                  radius="md"
                  className={`${primaryAction.primaryOutline} ${styles.emptyCta}`}
                  onClick={() => handleOpenLibrary("upload")}
                >
                  Upload your own image
                </Button>
              </Group>
            </div>
          </div>
        )}
      </div>

      <DiagramLibrary
        opened={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleSelection}
        defaultTab={libraryDefaultTab}
      />

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete chart"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete &ldquo;{deleteTarget?.title}&rdquo;?
            This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDeleting}
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
